/**
 * GolfToursAdmin — manage up to 8 professional tours (Golf Tours section).
 * Each tour: slug, display name, leaderboard URL (used by future agent),
 * official site URL (the user-facing CTA), optional logo URL, order, active.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRESETS = [
  { slug: 'dpwt',     name: 'DP World Tour',  leaderboard_url: 'https://www.europeantour.com/dpworld-tour/leaderboard/' },
  { slug: 'pga',      name: 'PGA Tour',       leaderboard_url: 'https://www.pgatour.com/leaderboard' },
  { slug: 'alps',     name: 'Alps Tour',      leaderboard_url: 'https://www.alpstourgolf.com/en/leaderboard' },
  { slug: 'asian',    name: 'Asian Tour',     leaderboard_url: 'https://asiantour.com/leaderboard/' },
  { slug: 'liv',      name: 'LIV Golf',       leaderboard_url: 'https://www.livgolf.com/scores' },
  { slug: 'staysure', name: 'Staysure Tour',  leaderboard_url: 'https://www.europeantour.com/staysure-tour/leaderboard/' },
];

const empty = () => ({ slug: '', name: '', leaderboard_url: '', official_url: '', logo_url: '', order: 0, active: true, parser: 'generic' });

export const GolfToursAdmin = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/zona-pro/tours`, { headers: { Authorization: `Bearer ${token}` } });
      setTours(r.data?.tours || []);
    } catch { toast.error('No s\'han pogut carregar els tours'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (t) => {
    const token = localStorage.getItem('token');
    const payload = { ...t, official_url: t.official_url || t.leaderboard_url };
    try {
      if (t.id) {
        await axios.put(`${API_URL}/api/zona-pro/tours/${t.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/api/zona-pro/tours`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Guardat');
      setDraft(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminar aquest tour?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/zona-pro/tours/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Eliminat'); await load();
    } catch { toast.error('Error'); }
  };

  const seedAll = async () => {
    if (!window.confirm('Afegir els 6 tours per defecte (PGA, DPWT, Alps, Asian, LIV, Staysure)?')) return;
    const existingSlugs = new Set(tours.map((t) => t.slug));
    for (const p of PRESETS) {
      if (existingSlugs.has(p.slug)) continue;
      await save({ ...empty(), ...p, official_url: p.leaderboard_url, parser: 'generic' });
    }
    toast.success('Tours sembrats');
  };

  return (
    <div className="space-y-4" data-testid="golf-tours-admin">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-slate-600 flex-1">Configura fins a 8 circuits. Apareixeran a la home a la secció &quot;Golf Tours&quot;.</p>
        {tours.length === 0 && (
          <Button onClick={seedAll} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tours-seed-all">
            ⚡ Afegir els 6 per defecte
          </Button>
        )}
        <Button onClick={() => setDraft(empty())} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tours-create-btn">+ Nou tour</Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Carregant…</div>
      ) : tours.length === 0 && !draft ? (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <p>Encara no hi ha tours. Clica <strong>&quot;Afegir els 6 per defecte&quot;</strong> per començar.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tours.map((t) => (
            <Card key={t.id} data-testid={`tour-row-${t.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {t.logo_url ? <img src={t.logo_url} alt="" className="w-12 h-12 object-contain bg-slate-100 rounded" /> : <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center text-xl">🏌️</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{t.name} <span className="text-xs text-slate-400">({t.slug})</span></div>
                    <div className="text-xs text-slate-500 truncate">{t.official_url || t.leaderboard_url}</div>
                    <div className="text-[11px] text-slate-400">ordre: {t.order} · {t.active ? 'actiu' : 'inactiu'}</div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setDraft(t)}>Editar</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(t.id)}>Eliminar</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {draft && (
        <Card className="border-emerald-300 mt-4">
          <CardContent className="p-4 space-y-3">
            <h4 className="font-medium">{draft.id ? 'Editar tour' : 'Nou tour'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Slug *</Label><Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder="pga" /></div>
              <div><Label className="text-xs">Nom *</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="PGA Tour" /></div>
              <div className="col-span-2"><Label className="text-xs">URL leaderboard / web oficial *</Label><Input value={draft.leaderboard_url} onChange={(e) => setDraft({ ...draft, leaderboard_url: e.target.value })} placeholder="https://..." /></div>
              <div className="col-span-2"><Label className="text-xs">URL web oficial (opcional, defecte=leaderboard)</Label><Input value={draft.official_url} onChange={(e) => setDraft({ ...draft, official_url: e.target.value })} placeholder="https://..." /></div>
              <div className="col-span-2"><Label className="text-xs">URL logo (opcional)</Label><Input value={draft.logo_url || ''} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} placeholder="https://..." /></div>
              <div><Label className="text-xs">Ordre</Label><Input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value) || 0 })} /></div>
              <div className="flex items-end gap-2"><input id="t-active" type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /><Label htmlFor="t-active" className="cursor-pointer">Actiu</Label></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDraft(null)}>Cancel·lar</Button>
              <Button onClick={() => save(draft)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Guardar</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GolfToursAdmin;
